import React, { useMemo } from "react";
import PropTypes from "prop-types";

// Utility to group data by first letter of "Status Reason"
const groupDataByLetter = (data, filterHeader) => {
    const groupedData = data.reduce((acc, item) => {
        const statusReason = item[filterHeader];
        if (!statusReason) {
            return acc;
        }
        const firstLetter = statusReason?.[0]?.toUpperCase();
        if (firstLetter) {
            if (!acc[firstLetter]) {
                acc[firstLetter] = [];
            }
            acc[firstLetter].push(item);
        }
        return acc;
    }, {});
    const sortedGrouped = Object.keys(groupedData)
        .sort((a, b) => a.localeCompare(b))
        .reduce((acc, key) => {
            acc[key] = groupedData[key];
            return acc;
        }, {});

    return sortedGrouped;
};

const normalizeFilePath = (filePath) => {
    if (!filePath) return "";

    const trimmedFilePath = String(filePath).trim();
    if (!trimmedFilePath) return "";

    if (/^(?:[a-z][a-z\d+.-]*:|\/\/|\/)/i.test(trimmedFilePath)) {
        return trimmedFilePath;
    }

    if (/^www\./i.test(trimmedFilePath)) {
        return `https://${trimmedFilePath}`;
    }

    return `/${trimmedFilePath.replace(/^\.?\//, "")}`;
};

const getDownloadFilePath = (row) =>
    normalizeFilePath(row?.filePath || row?.FilePath);

const isSameSiteFilePath = (filePath) =>
    filePath.startsWith("/") ||
    filePath.startsWith(window.location.origin) ||
    filePath.startsWith("blob:") ||
    filePath.startsWith("data:");

const triggerHiddenFrameDownload = (filePath) => {
    const frame = document.createElement("iframe");
    frame.src = filePath;
    frame.title = "file-download";
    frame.style.display = "none";
    document.body.appendChild(frame);

    window.setTimeout(() => {
        frame.remove();
    }, 60000);
};

const DynamicTable = ({ columns, data, responsiveConfig, filterHeader }) => {
    // Always call the useMemo hooks unconditionally
    const groupedData = useMemo(
        () => groupDataByLetter(data, filterHeader),
        [data]
    );

    // Convert groupedData to an array of rows with their respective subheaders
    const groupedRows = useMemo(
        () =>
            Object.keys(groupedData).map((letter) => ({
                letter,
                rows: groupedData[letter],
            })),
        [groupedData]
    );

    // Check if data is empty and return a message early
    if (!Array.isArray(data) || data?.length === 0) {
        return <div>No data available</div>;
    }

    // Utility function to handle line breaks
    const handleLineBreaks = (text) => {
        if (!text) return "-";
        return text.split(String.raw`\n`).map((line, index) => {
            const trimmedText = line.trim();
            return (
                <div key={`${trimmedText}-${index}`}>
                    {trimmedText === "" ? <br /> : line}
                </div>
            );
        });
    };

    const handleFileDownload = (event, filePath) => {
        if (isSameSiteFilePath(filePath)) return;

        event.preventDefault();
        triggerHiddenFrameDownload(filePath);
    };

    return (
        <div className={`table-container ${responsiveConfig?.screen}`}>
            <table
                className={`table ${responsiveConfig?.striped ? "striped" : ""}`}
                style={{ width: "100%" }}
            >
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th
                                key={column.Header}
                                colSpan="1"
                                style={{
                                    width: column.width || "auto",
                                    color: "#015294",
                                    fontSize: "16px",
                                    fontWeight: 700,
                                    lineHeight: "28px",
                                    padding: "20px 20px 20px",
                                    textAlign: "left",
                                }}
                            >
                                {column.Header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {groupedRows.map((group) => (
                        <React.Fragment key={group.letter}>
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    style={{
                                        textAlign: "left",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {group.letter}
                                </td>
                            </tr>
                            {group.rows.map((row) => (
                                <tr
                                    key={row?.id}
                                    style={{
                                        backgroundColor:
                                            group.rows.indexOf(row) % 2 === 0
                                                ? "#f6f8fa"
                                                : "#ffffff",
                                        padding: "8px 20px",
                                        fontSize: "16px",
                                        lineHeight: "25px",
                                        color: "#212529",
                                    }}
                                >
                                    {columns.map((column) => {
                                        const downloadFilePath =
                                            column.accessor === "Definition"
                                                ? getDownloadFilePath(row)
                                                : "";

                                        return (
                                            <td key={column.accessor}>
                                                {handleLineBreaks(
                                                    row[column.accessor]
                                                )}
                                                {downloadFilePath && (
                                                    <div
                                                        style={{
                                                            marginTop: "8px",
                                                        }}
                                                    >
                                                        Click here for help with{" "}
                                                        <span className="help-table-pdf-link">
                                                            <a
                                                                className="help-table-pdf-link"
                                                                href={
                                                                    downloadFilePath
                                                                }
                                                                {...(isSameSiteFilePath(
                                                                    downloadFilePath
                                                                )
                                                                    ? {
                                                                          download: true,
                                                                      }
                                                                    : {
                                                                          onClick:
                                                                              (
                                                                                  event
                                                                              ) =>
                                                                                  handleFileDownload(
                                                                                      event,
                                                                                      downloadFilePath
                                                                                  ),
                                                                      })}
                                                            >
                                                                Total Leave
                                                                Weeks to Hours
                                                                Conversion
                                                            </a>
                                                        </span>
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

DynamicTable.propTypes = {
    columns: PropTypes.array,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    responsiveConfig: PropTypes.shape({
        screen: PropTypes.string,
        striped: PropTypes.bool,
    }),
    filterHeader: PropTypes.string.isRequired,
};

export default DynamicTable;
